/**
 * TalentLens AI - Server Logs Page
 * Admin-only view for processing logs, errors, and system status
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

type LogTab = 'processing' | 'errors' | 'app' | 'system';

export default function ServerLogsPage() {
  const [activeTab, setActiveTab] = useState<LogTab>('processing');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [logLevel, setLogLevel] = useState<string>('error');

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Server Logs</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Monitor CV processing, errors, and system health</p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {[
          { key: 'processing', label: 'Processing Logs' },
          { key: 'errors', label: 'Errors' },
          { key: 'app', label: 'App Logs' },
          { key: 'system', label: 'System Status' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as LogTab)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'processing' && <ProcessingLogsTab statusFilter={statusFilter} setStatusFilter={setStatusFilter} />}
      {activeTab === 'errors' && <ErrorLogsTab />}
      {activeTab === 'app' && <AppLogsTab logLevel={logLevel} setLogLevel={setLogLevel} />}
      {activeTab === 'system' && <SystemStatusTab />}
    </div>
  );
}

function ProcessingLogsTab({ statusFilter, setStatusFilter }: { statusFilter: string; setStatusFilter: (s: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'processing-logs', statusFilter],
    queryFn: () => api.getProcessingLogs({ limit: '100', ...(statusFilter && { status: statusFilter }) }),
    refetchInterval: 10000,
  });

  const logs = data?.data?.logs || [];

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <span className="text-sm text-gray-500">{logs.length} logs (auto-refreshing)</span>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No processing logs found</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">CV</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Attempt</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Duration</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Error</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {logs.map((log: any) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                  <td className="px-4 py-3">
                    <LogStatusBadge status={log.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-900 dark:text-white font-mono text-xs">
                    {log.cvId?.metadata?.filename || log.cvId?._id || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{log.attempt}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {log.processingTimeMs ? `${(log.processingTimeMs / 1000).toFixed(1)}s` : '-'}
                  </td>
                  <td className="px-4 py-3 text-red-600 dark:text-red-400 text-xs max-w-xs truncate" title={log.error}>
                    {log.error || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ErrorLogsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'error-logs'],
    queryFn: () => api.getErrorLogs({ limit: '100' }),
    refetchInterval: 10000,
  });

  const errors = data?.data?.errors || [];

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">{errors.length} failed processing attempts (auto-refreshing)</p>
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : errors.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No errors found</div>
      ) : (
        <div className="space-y-3">
          {errors.map((err: any) => (
            <div key={err.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-red-500">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">
                    {err.error || 'Unknown error'}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>CV: {err.cvId?.metadata?.filename || err.cvId?._id || '-'}</span>
                    <span>Attempt #{err.attempt}</span>
                    {err.processingTimeMs && <span>{(err.processingTimeMs / 1000).toFixed(1)}s</span>}
                  </div>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {err.createdAt ? new Date(err.createdAt).toLocaleString() : '-'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AppLogsTab({ logLevel, setLogLevel }: { logLevel: string; setLogLevel: (s: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'app-logs', logLevel],
    queryFn: () => api.getAppLogs({ lines: '100', level: logLevel }),
    refetchInterval: 5000,
  });

  const logs = data?.data?.logs || [];
  const message = data?.data?.message;

  const levelColors: Record<number, string> = {
    60: 'text-red-800 bg-red-100',   // fatal
    50: 'text-red-700 bg-red-50',     // error
    40: 'text-yellow-700 bg-yellow-50', // warn
    30: 'text-blue-700 bg-blue-50',   // info
    20: 'text-gray-600 bg-gray-50',   // debug
    10: 'text-gray-400 bg-gray-50',   // trace
  };

  const levelNames: Record<number, string> = {
    60: 'FATAL', 50: 'ERROR', 40: 'WARN', 30: 'INFO', 20: 'DEBUG', 10: 'TRACE',
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <select
          value={logLevel}
          onChange={(e) => setLogLevel(e.target.value)}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
        >
          <option value="error">Error & above</option>
          <option value="warn">Warn & above</option>
          <option value="info">Info & above</option>
          <option value="debug">Debug & above</option>
        </select>
        <span className="text-sm text-gray-500">{logs.length} entries (auto-refreshing every 5s)</span>
      </div>

      {message && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded-lg mb-4 text-sm">
          {message}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-gray-900 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 max-h-[600px] overflow-y-auto font-mono text-xs space-y-1">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No log entries</div>
            ) : (
              logs.map((log: any, i: number) => (
                <div key={i} className="flex gap-2 leading-relaxed">
                  <span className="text-gray-500 flex-shrink-0">
                    {log.time ? new Date(log.time).toLocaleTimeString() : ''}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${levelColors[log.level] || 'text-gray-400'}`}>
                    {levelNames[log.level] || log.level}
                  </span>
                  <span className="text-gray-300 break-all">
                    {log.msg || JSON.stringify(log)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SystemStatusTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'system-status'],
    queryFn: () => api.getSystemStatus(),
    refetchInterval: 5000,
  });

  const status = data?.data;

  if (isLoading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!status) return <div className="text-center py-12 text-gray-500">Failed to load status</div>;

  const memMB = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Queue Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Queue Status</h3>
        <div className="grid grid-cols-2 gap-4">
          <StatusCard label="Waiting" value={status.queue.waiting} color="yellow" />
          <StatusCard label="Active" value={status.queue.active} color="blue" />
          <StatusCard label="Completed" value={status.queue.completed} color="green" />
          <StatusCard label="Failed" value={status.queue.failed} color="red" />
        </div>
      </div>

      {/* Health Indicators */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Health</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Failures (24h)</span>
            <span className={`font-semibold ${status.recentFailures24h > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {status.recentFailures24h}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Stuck Processing</span>
            <span className={`font-semibold ${status.stuckProcessing > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
              {status.stuckProcessing}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Uptime</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {(status.uptime / 3600).toFixed(1)}h
            </span>
          </div>
        </div>
      </div>

      {/* Memory Usage */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 md:col-span-2">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Memory Usage</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">RSS</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{memMB(status.memory.rss)} MB</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Heap Used</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{memMB(status.memory.heapUsed)} MB</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Heap Total</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{memMB(status.memory.heapTotal)} MB</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">External</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{memMB(status.memory.external)} MB</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses: Record<string, string> = {
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
  };

  return (
    <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
      <p className="text-xs opacity-75 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function LogStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    queued: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.queued}`}>
      {status}
    </span>
  );
}
